import React, { useState, useMemo } from 'react';
import DimensionHeader from '../../common/DimensionHeader';
import CollapsibleSection from './CollapsibleSection';
import { AnimatedNumber, AnimatedCard } from '../../common/AnimationUtils';
import { useTheme } from '../../../utils/ThemeSwitcher';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Info,
  GitBranch,
  TrendingUp,
} from 'lucide-react';
import './GatedFieldSection.css';
import CoachingPanel from './CoachingPanel';
import PulseBeacon from './PulseBeacon';
import { getTextOverrideForFlaggedRow } from '../../audio/FlaggedRowAudioButton';
import FlaggedRowActionsCell from '../../audio/FlaggedRowActionsCell';

/**
 * GatedFieldSection — Improvement 3 dashboard.
 * Renders results from `check_gated_fields`.
 *
 * Visual language follows CompletenessSection: shared DimensionHeader,
 * CollapsibleSection dividers, MetricCard chrome, AnimatedCard fade-ins.
 */
const GatedFieldSection = ({
  data, dataCollectorId, coachingSuggestions = [], isSidePanelExpanded, isMobile = false,
  primaryData = null, audioSettings = null, tauriApi = null, transcriptsMeta = null,
  projectId = null, userInfo = null, audioNotesDoc = null,
  onMetaUpdated = null, onAudioNotesUpdated = null,
}) => {
  const { isDarkMode } = useTheme();

  const results          = data?.overall || {};
  const overallStats     = results.overall || {};
  const byRule           = results.by_rule || {};
  const flaggedRows      = results.flagged_rows || [];
  const byCollector      = results.by_collector || {};
  const recommendations  = results.recommendations || [];
  const ruleErrors       = results.rule_errors || [];

  const [activeTab, setActiveTab]             = useState('summary');
  const [ruleFilter, setRuleFilter]           = useState('__all__');
  const [severityFilter, setSeverityFilter]   = useState('__all__');
  const [collectorFilter, setCollectorFilter] = useState('__all__');
  const [rowSearch, setRowSearch]             = useState('');

  const colors = {
    background:    isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg:        isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt:     isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border:        isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight:   isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text:          isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted:     isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error:   '#EF4444',
    accent:  isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  if (results.error) {
    return (
      <div className="gfs-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <XCircle size={48} style={{ color: colors.error, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>Gated-field check failed</h3>
          <p style={emptyTextStyle(colors)}>{results.error}</p>
        </div>
      </div>
    );
  }

  if (Object.keys(byRule).length === 0 && ruleErrors.length === 0) {
    return (
      <div className="gfs-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <GitBranch size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>No gate rules configured</h3>
          <p style={emptyTextStyle(colors)}>
            Define one or more rules in the detector and run a check to see results here.
          </p>
        </div>
      </div>
    );
  }

  const sevColor = (sev) => sev === 'high' ? colors.error
                          : sev === 'medium' ? colors.warning
                          : sev === 'low' ? '#FBBF24'
                          : colors.textMuted;

  const wcColor = (wc) => wc === 0 ? colors.error
                        : wc < 5 ? colors.error
                        : wc < 10 ? '#E06666'
                        : wc < 20 ? colors.warning
                        : wc < 30 ? '#FBBF24'
                        : colors.success;

  const qualityScore = overallStats.quality_score ?? 100;
  const qualityTone = qualityScore >= 90 ? colors.success
                    : qualityScore >= 70 ? colors.warning
                    : colors.error;

  const topFlaggedRules = useMemo(() =>
    Object.entries(byRule)
      .map(([rid, r]) => ({
        ruleId:    rid,
        label:     r.label || rid,
        flag_count:r.flag_count ?? 0,
        flag_rate: r.flag_rate ?? 0,
      }))
      .filter(x => x.flag_count > 0)
      .sort((a, b) => b.flag_rate - a.flag_rate)
      .slice(0, 5),
    [byRule]
  );

  const topFlaggedCollectors = useMemo(() =>
    Object.entries(byCollector)
      .map(([cid, cd]) => ({ collector: cid, flag_count: cd.flag_count ?? 0 }))
      .filter(x => x.flag_count > 0)
      .sort((a, b) => b.flag_count - a.flag_count)
      .slice(0, 5),
    [byCollector]
  );

  const filteredRows = useMemo(() => {
    let rows = flaggedRows;
    if (ruleFilter      !== '__all__') rows = rows.filter(r => r.rule_id === ruleFilter);
    if (severityFilter  !== '__all__') rows = rows.filter(r => r.severity === severityFilter);
    if (collectorFilter !== '__all__') rows = rows.filter(r => r.collector_id === collectorFilter);
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.response_preview || '').toLowerCase().includes(q) ||
        (r.follow_up_var || '').toLowerCase().includes(q) ||
        (r.gate_var || '').toLowerCase().includes(q) ||
        (r.respondent_id || '').toString().toLowerCase().includes(q)
      );
    }
    return rows;
  }, [flaggedRows, ruleFilter, severityFilter, collectorFilter, rowSearch]);

  // ----------------------------------------------------- shared MetricCard
  const MetricCard = ({ icon: Icon, title, value, subtitle, severity, animDelay = 0 }) => {
    const sevLight = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
    const sevDark  = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
    const iconColor = isDarkMode ? (sevDark || '#60a5fa') : (sevLight || '#2563EB');

    return (
      <AnimatedCard delay={animDelay} translateY={12}>
        <div className="gfs-metric-card" style={{
          position: 'relative', padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden', fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          {severity && (
            <svg width="30" height="30" viewBox="0 0 30 30" style={{
              position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible',
            }}>
              <path d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
                    stroke={iconColor} strokeWidth="2.5" fill="none"
                    strokeLinecap="round" opacity={0.9} />
            </svg>
          )}

          <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '5px',
              background: severity
                ? (isDarkMode ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                              : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
                : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, color: iconColor,
            }}>
              <Icon size={16} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div style={{
                fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
                textTransform: 'lowercase', color: colors.textMuted, marginBottom: '10px',
              }}>{title}</div>
              <div className="metric-value" style={{
                fontSize: '32px', fontWeight: 500, letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums', color: colors.text,
                lineHeight: 1.05, marginBottom: '6px',
              }}>
                {typeof value === 'number'
                  ? <AnimatedNumber value={value} duration={800} />
                  : value}
              </div>
              <div style={{
                fontSize: '11.5px', fontWeight: 400, color: colors.textMuted, lineHeight: 1.4,
              }}>{subtitle}</div>
            </div>
            <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
          </div>
        </div>
      </AnimatedCard>
    );
  };

  // ============================================================ tab: SUMMARY
  const renderSummary = () => (
    <div>
      {ruleErrors.length > 0 && (
        <div style={{
          padding: '14px 16px', marginBottom: '16px',
          backgroundColor: isDarkMode ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
          border: `1px solid ${colors.error}33`, borderRadius: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <XCircle size={16} style={{ color: colors.error }} />
            <strong style={{ color: colors.error, fontSize: '13px' }}>
              {ruleErrors.length} rule(s) could not run
            </strong>
          </div>
          <ul style={{
            fontSize: '12.5px', color: colors.text, lineHeight: 1.6,
            margin: 0, paddingLeft: '24px',
          }}>
            {ruleErrors.map((re, i) => (
              <li key={i}><strong>{re.label || re.rule_id}:</strong> {re.error}</li>
            ))}
          </ul>
        </div>
      )}

      <CollapsibleSection
        title="Key Metrics"
        isDarkMode={isDarkMode}
        colors={colors}
        badge={
          (overallStats.total_flags ?? 0) > 0 ? (
            <span style={{
              padding: '2px 8px', borderRadius: '10px',
              backgroundColor: '#EF444420', color: '#EF4444',
              fontSize: '11px', fontWeight: 600,
            }}>
              {overallStats.total_flags} flag{overallStats.total_flags === 1 ? '' : 's'}
            </span>
          ) : null
        }
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}>
          <MetricCard
            animDelay={0}
            icon={GitBranch}
            title="Rules Run"
            value={`${overallStats.total_rules_runnable ?? 0} / ${overallStats.total_rules ?? 0}`}
            subtitle={ruleErrors.length > 0 ? `${ruleErrors.length} rule error(s)` : 'All valid'}
            severity={ruleErrors.length > 0 ? 'warning' : null}
          />
          <MetricCard
            animDelay={80}
            icon={AlertTriangle}
            title="Total Flags"
            value={overallStats.total_flags ?? 0}
            subtitle={`${(overallStats.flag_rate ?? 0).toFixed(1)}% of in-scope rows`}
            severity={
              (overallStats.flag_rate ?? 0) > 20 ? 'critical' :
              (overallStats.flag_rate ?? 0) > 5  ? 'warning' : null
            }
          />
          <MetricCard
            animDelay={160}
            icon={CheckCircle}
            title="Quality Score"
            value={`${qualityScore}%`}
            subtitle={
              qualityScore >= 90 ? 'Excellent' :
              qualityScore >= 70 ? 'Good' :
              qualityScore >= 50 ? 'Fair' : 'Critical'
            }
            severity={
              qualityScore < 50 ? 'critical' :
              qualityScore < 70 ? 'warning' : null
            }
          />
          <MetricCard
            animDelay={240}
            icon={Info}
            title="Rows Evaluated"
            value={overallStats.total_rows_evaluated ?? 0}
            subtitle="In gate-trigger scope"
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
        <div className="gfs-analysis-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '16px',
        }}>
          {/* Top flagged rules */}
          <AnimatedCard delay={0} translateY={12}>
            <div className="gfs-top-issues" style={{
              padding: '20px', backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`, borderRadius: '12px',
            }}>
              <div style={subheaderStyle(colors)}>top issues</div>
              <div style={cardTitleStyle(colors)}>Top Rule Issues</div>
              {topFlaggedRules.length === 0 ? (
                <div style={{ fontSize: '13px', color: colors.success, padding: '20px 0' }}>
                  ✓ No flagged rules
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto auto',
                    fontSize: '11px', color: colors.textMuted, marginBottom: '8px',
                    paddingBottom: '8px', borderBottom: `1px solid ${colors.borderLight}`,
                  }}>
                    <div>rule</div>
                    <div style={{ textAlign: 'right' }}>flag rate</div>
                    <div style={{ textAlign: 'right', minWidth: '50px' }}>flags</div>
                  </div>
                  {topFlaggedRules.map((r, i) => (
                    <div key={i} className="gfs-row" style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto',
                      padding: '8px 0', fontSize: '12.5px', alignItems: 'center',
                    }}>
                      <div style={{
                        color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>{r.label}</div>
                      <div style={{
                        textAlign: 'right',
                        color: r.flag_rate > 20 ? colors.error : colors.warning,
                        fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: '60px',
                      }}>
                        <AnimatedNumber value={r.flag_rate} decimals={1} suffix="%" duration={900} delay={i * 60} />
                      </div>
                      <div style={{
                        textAlign: 'right', color: colors.textSecondary,
                        fontVariantNumeric: 'tabular-nums', minWidth: '50px',
                      }}>
                        <AnimatedNumber value={r.flag_count} duration={700} delay={i * 60} />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </AnimatedCard>

          {/* Top flagged collectors */}
          <AnimatedCard delay={120} translateY={12}>
            <div className="gfs-top-issues" style={{
              padding: '20px', backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`, borderRadius: '12px',
            }}>
              <div style={subheaderStyle(colors)}>top issues</div>
              <div style={cardTitleStyle(colors)}>Top Collector Issues</div>
              {topFlaggedCollectors.length === 0 ? (
                <div style={{ fontSize: '13px', color: colors.textMuted, padding: '20px 0' }}>
                  No collector data available
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto',
                    fontSize: '11px', color: colors.textMuted, marginBottom: '8px',
                    paddingBottom: '8px', borderBottom: `1px solid ${colors.borderLight}`,
                  }}>
                    <div>collector</div>
                    <div style={{ textAlign: 'right' }}>flags</div>
                  </div>
                  {topFlaggedCollectors.map((c, i) => (
                    <div key={i} className="gfs-row" style={{
                      display: 'grid', gridTemplateColumns: '1fr auto',
                      padding: '8px 0', fontSize: '12.5px',
                    }}>
                      <div style={{ color: colors.text }}>{c.collector}</div>
                      <div style={{
                        textAlign: 'right', color: colors.error,
                        fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                      }}>
                        <AnimatedNumber value={c.flag_count} duration={700} delay={i * 60} />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </AnimatedCard>
        </div>
      </CollapsibleSection>

      <CoachingPanel
        suggestions={coachingSuggestions}
        detectorSource="gated_fields"
        isMobile={isMobile}
      />
    </div>
  );

  // ============================================================ tab: BY RULE
  const renderRules = () => (
    <div style={tableWrapStyle(colors)}>
      <table style={tableStyle}>
        <thead>
          <tr style={theadRowStyle(isDarkMode)}>
            <th style={th(colors)}>Rule</th>
            <th style={th(colors)}>Mode</th>
            <th style={th(colors)}>Follow-up ← Gate</th>
            <th style={th(colors)}>Trigger</th>
            <th style={{ ...th(colors), textAlign: 'right' }}>In scope</th>
            <th style={{ ...th(colors), textAlign: 'right' }}>Flagged</th>
            <th style={{ ...th(colors), textAlign: 'right' }}>Flag rate</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byRule).map(([rid, r]) => (
            <tr key={rid} style={{ borderTop: `1px solid ${colors.border}` }}>
              <td style={td(colors)}>
                <div style={{ fontWeight: 600 }}>{r.label || rid}</div>
                <div style={{
                  color: colors.textMuted, fontSize: '11.5px',
                  fontFamily: 'ui-monospace, monospace',
                }}>{rid}</div>
                {r.error && (
                  <div style={{ color: colors.error, fontSize: '11.5px', marginTop: '4px' }}>
                    ⚠ {r.error}
                  </div>
                )}
              </td>
              <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>
                {r.mode || '—'}
              </td>
              <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>
                <div>{r.follow_up_var}</div>
                <div style={{ color: colors.textMuted, fontSize: '11px' }}>← {r.gate_var}</div>
              </td>
              <td style={td(colors)}>
                {(r.gate_values || []).map((gv, i) => (
                  <span key={i} style={{
                    display: 'inline-block', padding: '2px 7px',
                    marginRight: '4px', marginBottom: '2px',
                    borderRadius: '4px', fontSize: '11.5px',
                    background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    color: colors.text, fontFamily: 'ui-monospace, monospace',
                  }}>{String(gv)}</span>
                ))}
              </td>
              <td style={tdRight(colors)}>{r.rows_in_scope ?? 0}</td>
              <td style={{
                ...tdRight(colors), fontWeight: 600,
                color: r.flag_count > 0 ? colors.error : colors.textMuted,
              }}>{r.flag_count ?? 0}</td>
              <td style={tdRight(colors)}>{(r.flag_rate ?? 0).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ============================================================ tab: ROWS
  const renderRows = () => (
    <div>
      <div style={{
        display: 'flex', gap: '10px', flexWrap: 'wrap',
        alignItems: 'center', marginBottom: '14px',
      }}>
        <select value={ruleFilter} onChange={(e) => setRuleFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
          <option value="__all__">All rules ({flaggedRows.length})</option>
          {Object.entries(byRule).map(([rid, r]) => (
            <option key={rid} value={rid}>{r.label || rid} ({r.flag_count ?? 0})</option>
          ))}
        </select>

        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
          <option value="__all__">All severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        {Object.keys(byCollector).length > 0 && (
          <select value={collectorFilter} onChange={(e) => setCollectorFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All collectors</option>
            {Object.keys(byCollector).map(c => (
              <option key={c} value={c}>{c} ({byCollector[c].flag_count})</option>
            ))}
          </select>
        )}

        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '200px' }}>
          <Search size={14} style={searchIconStyle(colors)} />
          <input
            type="text"
            placeholder="Search responses, gate var, respondent ID..."
            value={rowSearch}
            onChange={(e) => setRowSearch(e.target.value)}
            style={searchInputStyle(colors)}
          />
        </div>
      </div>

      <div style={{ ...tableWrapStyle(colors), overflow: 'auto', maxHeight: '600px' }}>
        <table style={tableStyle}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr style={theadRowStyle(isDarkMode)}>
              <th style={th(colors)}>#</th>
              <th style={th(colors)}>Respondent</th>
              <th style={th(colors)}>Collector</th>
              <th style={th(colors)}>Rule</th>
              <th style={th(colors)}>Gate value</th>
              <th style={th(colors)}>Flag type</th>
              <th style={{ ...th(colors), textAlign: 'center' }}>Severity</th>
              <th style={{ ...th(colors), textAlign: 'center' }}>WC</th>
              <th style={th(colors)}>Follow-up preview</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={10} style={emptyRowStyle(colors)}>
                  No flagged rows match the current filters.
                </td>
              </tr>
            )}
            {filteredRows.map((r, i) => (
              <tr key={i} className="gfs-table-row" style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ ...td(colors), color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                  {r.row_index}
                </td>
                <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>
                  {r.respondent_id || '—'}
                </td>
                <td style={td(colors)}>{r.collector_id || '—'}</td>
                <td style={td(colors)}>
                  <div style={{ fontSize: '12.5px' }}>{r.rule_label || r.rule_id}</div>
                  <div style={{
                    fontSize: '11px', color: colors.textMuted,
                    fontFamily: 'ui-monospace, monospace',
                  }}>
                    {r.follow_up_var} ← {r.gate_var}
                  </div>
                </td>
                <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>
                  {r.gate_value === null ? '—' : String(r.gate_value)}
                </td>
                <td style={{
                  ...td(colors), fontFamily: 'ui-monospace, monospace',
                  fontSize: '11.5px', color: colors.textSecondary,
                }}>{r.flag_type}</td>
                <td style={{ ...td(colors), textAlign: 'center' }}>
                  <span style={pillStyle(sevColor(r.severity))}>{r.severity}</span>
                </td>
                <td style={{
                  ...td(colors), textAlign: 'center',
                  fontWeight: 600, color: wcColor(r.word_count),
                }}>{r.word_count}</td>
                <td style={{ ...td(colors), maxWidth: '440px' }}>
                  {(() => {
                    const override = getTextOverrideForFlaggedRow(
                      r.follow_up_var, r.row_index, primaryData, transcriptsMeta);
                    const displayText = override?.text ?? r.response_preview ?? '';
                    return (
                      <span style={{ color: colors.text, lineHeight: 1.5, minWidth: 0 }}>
                        {(displayText || '').slice(0, 220) ||
                          <em style={{ color: colors.textMuted }}>(empty)</em>}
                        {(displayText || '').length > 220 && '…'}
                        {override && (
                          <span title={`Re-transcribed${override.languageHint ? ' in ' + override.languageHint : ''}. Refresh checks to update flag.`}
                                style={{
                                  marginLeft: '8px',
                                  padding: '1px 7px',
                                  fontSize: '9.5px',
                                  fontWeight: 600,
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                  background: 'rgba(217,119,6,0.12)',
                                  color: '#D97706',
                                  border: '1px solid rgba(217,119,6,0.30)',
                                  borderRadius: '999px',
                                  cursor: 'help',
                                }}>
                            re-transcribed
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ ...td(colors), textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <FlaggedRowActionsCell
                    variable={r.follow_up_var}
                    rowIndex={r.row_index}
                    submissionId={r.respondent_id}
                    primaryData={primaryData}
                    audioSettings={audioSettings}
                    tauriApi={tauriApi}
                    isDarkMode={isDarkMode}
                    transcriptsMeta={transcriptsMeta}
                    projectId={projectId}
                    userInfo={userInfo}
                    audioNotesDoc={audioNotesDoc}
                    onMetaUpdated={onMetaUpdated}
                    onAudioNotesUpdated={onAudioNotesUpdated}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================ tab: COLLECTORS
  const renderCollectors = () => (
    <div style={tableWrapStyle(colors)}>
      <table style={tableStyle}>
        <thead>
          <tr style={theadRowStyle(isDarkMode)}>
            <th style={th(colors)}>Collector</th>
            <th style={{ ...th(colors), textAlign: 'right' }}>Flag count</th>
            <th style={{ ...th(colors), textAlign: 'right' }}>Share of total</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byCollector)
            .sort(([, a], [, b]) => (b.flag_count || 0) - (a.flag_count || 0))
            .map(([cid, cd]) => {
              const share = (overallStats.total_flags ?? 0) > 0
                ? (cd.flag_count / overallStats.total_flags) * 100
                : 0;
              return (
                <tr key={cid} style={{ borderTop: `1px solid ${colors.border}` }}>
                  <td style={td(colors)}>{cid}</td>
                  <td style={{
                    ...tdRight(colors), fontWeight: 600,
                    color: cd.flag_count > 0 ? colors.error : colors.textMuted,
                  }}>{cd.flag_count}</td>
                  <td style={tdRight(colors)}>{share.toFixed(1)}%</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );

  // ============================================================ render
  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'rules',   label: 'By Rule',
      count: Object.keys(byRule).length, countTone: 'neutral' },
    { id: 'rows',    label: 'Flagged Rows',
      count: flaggedRows.length, countTone: flaggedRows.length > 0 ? 'alert' : 'neutral' },
    ...(Object.keys(byCollector).length > 0
      ? [{ id: 'collectors', label: 'By Collector',
           count: Object.keys(byCollector).length, countTone: 'neutral' }]
      : []),
  ];

  return (
    <div className="gfs-section" style={{
      padding: isMobile ? '12px' : '0 4px',
      boxSizing: 'border-box',
      overflow: isMobile ? 'visible' : 'hidden',
    }}>
      <DimensionHeader
        slug="gated-fields"
        title="Gated-Field Consistency"
        description='Pairs a "yes/no" gate question with its follow-up field — flags rows where the answers contradict each other (e.g. respondent says "yes I received training" but the description audio is missing). Use rule-based config: each rule names its own follow-up + gate columns and trigger values.'
        rememberCollapseKey="gated-fields"
        stat={{
          score: qualityScore,
          scoreLabel: 'quality score',
          unit: '%',
        }}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      <div style={{ minHeight: '600px', marginTop: '16px' }}>
        {activeTab === 'summary'    && renderSummary()}
        {activeTab === 'rules'      && renderRules()}
        {activeTab === 'rows'       && renderRows()}
        {activeTab === 'collectors' && renderCollectors()}
      </div>
    </div>
  );
};

// ----------------------------------------------------------- shared styles
const emptyWrapStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '400px', padding: '32px',
};
const emptyCardStyle = (colors) => ({
  textAlign: 'center', padding: '32px', borderRadius: '16px',
  backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`,
});
const emptyTitleStyle = (colors) => ({
  fontSize: '16px', fontWeight: 600, color: colors.text, marginBottom: '8px',
});
const emptyTextStyle = (colors) => ({ fontSize: '13px', color: colors.textSecondary });

const subheaderStyle = (colors) => ({
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em',
  color: colors.textMuted, marginBottom: '4px', fontWeight: 500,
});
const cardTitleStyle = (colors) => ({
  fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '14px',
});

const th = (colors) => ({
  padding: '12px 14px', textAlign: 'left', fontWeight: 600,
  color: colors.text, fontSize: '12px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
});
const td = (colors) => ({ padding: '11px 14px', color: colors.text });
const tdRight = (colors) => ({
  padding: '11px 14px', textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', color: colors.text,
});

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const tableWrapStyle = (colors) => ({
  backgroundColor: colors.cardBg,
  border: `1px solid ${colors.border}`,
  borderRadius: '12px',
  overflow: 'hidden',
});
const theadRowStyle = (isDarkMode) => ({
  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
});
const emptyRowStyle = (colors) => ({
  padding: '32px', textAlign: 'center', color: colors.textMuted,
});

const pillStyle = (color) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
  fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
  background: `${color}22`, color,
});

const searchIconStyle = (colors) => ({
  position: 'absolute', left: '10px', top: '50%',
  transform: 'translateY(-50%)', color: colors.textMuted,
});
const searchInputStyle = (colors) => ({
  width: '100%', padding: '8px 12px 8px 32px',
  borderRadius: '8px', border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  fontFamily: 'inherit',
});

const selectStyle = (isDarkMode, colors) => ({
  padding: '8px 12px', borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  cursor: 'pointer', fontFamily: 'inherit', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='${isDarkMode ? '%239CA3AF' : '%236B7280'}' d='M6 9L1 4h10z'/></svg>")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '28px',
});

export default GatedFieldSection;